import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useSubscription } from '../../../../../store'
import { MentionableItem, MentionableShortcut, Skill } from '../../../../../types'
import { useShortcuts } from '../../../../../hooks/useShortcuts'
import { useAvailableSkills } from '../../../../../hooks/useSkills'
import { CONTEXT_PLUGINS, backendPluginsList } from '../backend-plugins'
import { mentionSearchAtom, slashModeAtom, useMessageMentions } from '../atoms'
import { useLanguageModels, useMentionableBrowserTabs } from './use-mention-sources'
import { useModelChoice } from '../../../../../hooks/use-plan-features'
import { getModelIdForMessage } from '../../../../../utils/chat'
import { useDefaultModelId } from './use-default-model-id'
import { SKILLS_SETTINGS_TAB } from '@shared/settings/settings-deeplink'

/** One labelled block of the menu, already gated and search-filtered. */
export interface MentionSection {
  label: string
  items: Array<MentionableItem>
  linkToPage?: string
  /** Where this section starts in the flat keyboard-navigation list. */
  offset: number
}

const matches = (query: string) => {
  if (!query.length) return () => true
  const needle = query.toLowerCase()
  return (mention: MentionableItem) => mention.label.toLowerCase().includes(needle)
}

// Maps a Skill to a MentionableShortcut, deduping against the provided id/name sets.
// Returns null when the skill is a duplicate of an already-seen entry, or when the
// user disabled it for user-invocation (so it belongs in neither menu).
const skillToMentionable = (skill: Skill, seenIds: Set<string>, seenNames: Set<string>): MentionableShortcut | null => {
  if (!skill.availability.userInvoke) return null
  const normalizedName = skill.name.trim().toLowerCase()
  if (seenIds.has(skill.id) || seenNames.has(normalizedName)) return null
  seenIds.add(skill.id)
  seenNames.add(normalizedName)
  return toSkillMention(skill)
}

/** The Settings tab that lists every skill, where the mention menus' View all goes. */
export const SKILLS_PAGE_LINK = `highlight://settings/${SKILLS_SETTINGS_TAB}`

/** A skill as the mention a message carries when the user invokes it with `/`. */
export const toSkillMention = (skill: Skill): MentionableShortcut => ({
  label: skill.name,
  value: skill.id,
  skillId: skill.id,
  images: { image: skill.imageUrl ?? null, userImages: null },
  agent_tools: null,
  type: 'shortcut',
})

/**
 * The one place the menu's contents are derived. `sections` IS what renders and
 * `combinedData` is its flattening, so the keyboard highlight can't drift onto a
 * row that isn't on screen — a section that shouldn't show simply isn't built.
 */
export const useFilteredMentionData = () => {
  const { data: shortcuts } = useShortcuts()
  const skills = useAvailableSkills()
  const mentionSearch = useAtomValue(mentionSearchAtom)
  const { subscription } = useSubscription()
  const slashMode = useAtomValue(slashModeAtom)
  const models = useLanguageModels()
  const browserTabs = useMentionableBrowserTabs()
  const messageMentions = useMessageMentions()
  const defaultModel = useDefaultModelId()
  const modelChoice = useModelChoice()
  const selectableModels = useMemo(() => (modelChoice ? models : []), [modelChoice, models])

  const hasProAccess = subscription.active
  const hasModelSelected = messageMentions.some((mention) => mention.type === 'model')
  const hasSelectedProModelWithoutAccess =
    !hasProAccess && messageMentions.some((mention) => mention.type === 'model' && mention.pricing?.isFree === false)

  // Plugins need a tool-capable model. With no model known (none picked, or the
  // plan picks it) we assume capable rather than hide the section.
  const toolsAvailable = useMemo(() => {
    const nextModelId = getModelIdForMessage({
      mentions: messageMentions,
      isExistingChat: false,
      conversationId: '',
      allMessages: {},
      defaultModel,
      modelChoice,
    })
    return (
      !nextModelId ||
      models.some((model) => model.id === nextModelId && model.capabilities?.supportsFunctionCalling === true)
    )
  }, [messageMentions, models, defaultModel, modelChoice])

  // Skills-only list for the `/` slash menu (excludes shortcuts)
  const filteredSkills = useMemo(() => {
    const seenIds = new Set<string>()
    const seenNames = new Set<string>()
    return skills
      .map((skill) => skillToMentionable(skill, seenIds, seenNames))
      .filter((item): item is MentionableShortcut => item !== null)
      .filter(matches(mentionSearch))
  }, [skills, mentionSearch])

  // Shortcuts and skills share one dedupe pass: a skill named after a shortcut
  // is dropped rather than listed twice.
  const filteredShortcuts = useMemo(() => {
    const items: Array<MentionableShortcut> = []
    const seenIds = new Set<string>()
    const seenNames = new Set<string>()

    for (const shortcut of shortcuts ?? []) {
      const normalizedName = shortcut.name.trim().toLowerCase()
      if (seenIds.has(shortcut.id) || seenNames.has(normalizedName)) continue
      seenIds.add(shortcut.id)
      seenNames.add(normalizedName)
      items.push({
        label: shortcut.name,
        value: shortcut.id,
        images: { image: shortcut.imageUrl, userImages: null },
        agent_tools: null,
        type: 'shortcut',
      })
    }

    for (const skill of skills) {
      const item = skillToMentionable(skill, seenIds, seenNames)
      if (item) items.push(item)
    }

    return items.filter(matches(mentionSearch))
  }, [shortcuts, skills, mentionSearch])

  // Pro tools and pro models, shown together at the top — only to users who have them.
  const filteredProFeatures = useMemo(() => {
    if (!hasProAccess) return []
    const proPlugins = backendPluginsList.filter((plugin) => plugin.isPro)
    const proModels = selectableModels.filter((model) => model.pricing?.isFree === false)
    return [...proPlugins, ...proModels].filter(matches(mentionSearch))
  }, [selectableModels, mentionSearch, hasProAccess])

  // Models, minus any already promoted into the PRO section above.
  const filteredModels = useMemo(() => {
    const searchFiltered = selectableModels.filter(matches(mentionSearch))
    if (hasSelectedProModelWithoutAccess) {
      return searchFiltered.filter((model) => model.pricing?.isFree !== false)
    }
    if (hasProAccess) {
      return searchFiltered.filter((model) => model.pricing?.isFree === true)
    }
    return searchFiltered
  }, [selectableModels, mentionSearch, hasSelectedProModelWithoutAccess, hasProAccess])

  const filteredContextPlugins = useMemo(
    () => backendPluginsList.filter((plugin) => CONTEXT_PLUGINS.includes(plugin.value)).filter(matches(mentionSearch)),
    [mentionSearch],
  )

  const filteredRegularPlugins = useMemo(() => {
    return backendPluginsList
      .filter((plugin) => !CONTEXT_PLUGINS.includes(plugin.value) && !(hasProAccess && plugin.isPro))
      .filter(matches(mentionSearch))
  }, [mentionSearch, hasProAccess])

  const filteredBrowserTabs = useMemo(() => browserTabs.filter(matches(mentionSearch)), [browserTabs, mentionSearch])

  // Section order here IS the visual order and the keyboard walk order.
  const sections = useMemo(() => {
    const ordered: Array<Omit<MentionSection, 'offset'>> = slashMode
      ? [{ label: 'Skills', items: filteredSkills, linkToPage: SKILLS_PAGE_LINK }]
      : [
          { label: 'PRO Features', items: filteredProFeatures },
          { label: 'Browser Tabs', items: filteredBrowserTabs },
          { label: 'Context Search', items: filteredContextPlugins },
          { label: 'Models', items: hasModelSelected ? [] : filteredModels, linkToPage: 'highlight://settings/models' },
          {
            label: 'Plugins',
            items: toolsAvailable ? filteredRegularPlugins : [],
            linkToPage: 'highlight://settings/connections',
          },
          { label: 'Skills & Shortcuts', items: filteredShortcuts, linkToPage: SKILLS_PAGE_LINK },
        ]

    let offset = 0
    return ordered
      .filter((section) => section.items.length > 0)
      .map((section) => {
        const withOffset = { ...section, offset }
        offset += section.items.length
        return withOffset
      })
  }, [
    slashMode,
    filteredSkills,
    filteredProFeatures,
    filteredBrowserTabs,
    filteredContextPlugins,
    filteredModels,
    filteredRegularPlugins,
    filteredShortcuts,
    hasModelSelected,
    toolsAvailable,
  ])

  const combinedData = useMemo(() => sections.flatMap((section) => section.items), [sections])

  return { sections, combinedData }
}
